import { updateMusicPlayer } from '../../scripts/musicPlayer';
import { getPlayerProvider } from './providers/providerUtils';
import { getPlaylists, addPlaylist, deletePlaylist } from '../../scripts/playlist';

const updatableProps = ['state', 'volume', 'songTitle', 'artists', 'album', 'playFrom', 'imageUrl', 'songColor'];

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const HOURS = 60 * MINUTES;

class MusicPlayer {
    constructor(playerInfo, callback) {
        this.name = playerInfo.name;
        this.hubId = playerInfo.hubId;
        this.displayName = playerInfo.displayName;
        this.type = playerInfo.type;
        this.customerId = playerInfo.customerId;
        this.musicPlayerId = playerInfo.musicPlayerId;
        this.remoteId = playerInfo.remoteId;
        this.sourceId = playerInfo.sourceId;
        this.sourceDisplayName = playerInfo.sourceDisplayName;
        this.defaultVolume = playerInfo.defaultVolume;
        this.state = undefined;
        this.isAuthorized = undefined;
        this.isConnected = undefined;
        this.isSubscribing = false;
        this.token = playerInfo.token;
        this.refreshToken = playerInfo.refreshToken;
        this.playerProvider = getPlayerProvider(playerInfo);
        this.music = {};
        this.callback = callback;
        this.reinitiationInterval = undefined;
        this.hasStoredPlaylists = false;

        this.virtualDjMode = playerInfo.virtualDjMode;
        this.virtualDjDataSource = playerInfo.virtualDjDataSource;
        this.virtualDjDataZones = playerInfo.virtualDjDataZones;
        this.playsInZones = playerInfo.playsInZones;

    }

    async init() {
        try {
            const initialIsAuthorized = await this.checkAuthorization();
            this.startReinitiationInterval();
            if (initialIsAuthorized !== true) {
                await this.updateAuthorization();
                const isAuthorized = await this.checkAuthorization();
                if (isAuthorized === true) {
                    return await this.onAuthorize();
                } else {
                    return false;
                }
            } else {
                return await this.onAuthorize();
            }
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    async onAuthorize() {
        try {
            const connected = await this.checkConnection();

            if (connected === true) {
                await this.getMusic();
                return true;
            } else {
                return false;
            }
        } catch (error) {
            return false;
        }
    }

    startReinitiationInterval() {
        if (this.reinitiationInterval) clearInterval(this.reinitiationInterval);
        this.reinitiationInterval = setInterval(() => {
           this.cleanup();
           this.init();
        }, 24 * HOURS);
    }

    async getMusic() {
        await this.getPlayingMusic();
        await this.getAvailablePlaylists();
        await this.getAvailableSchedules();
        await this.subscribe();
    }

    async play() {
        try {
            const result = await this.playerProvider.play();
            if (result) return true;
            else return false;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async pause() {
        try {
            const result = await this.playerProvider.pause();
            if (result) return true;
            else return false;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async nextSong() {
        try {
            const result = await this.playerProvider.nextSong();
            if (result) return true;
            else return false;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async getState() {
        try {
            const state = await this.playerProvider.getState();
            if (state === 'PLAYING' || state === 'PAUSED') {
                this.state = state;
                return this.state;
            }
            else return false;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async getVolume() {
        try {
            var volume = await this.playerProvider.getVolume();
            if (typeof (volume) != "number") {
                volume = Number(volume);
            }
            this.volume = volume;
            return this.volume;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async setVolume(volume) {
        try {
            if (typeof (volume) != 'number') {
                return false;
            }
            const result = await this.playerProvider.setVolume(volume);
            if (result) {
                this.volume = volume;
                return true;
            }
            else {
                return false
            };
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async getPlayingMusic() {
        try {
            const music = await this.playerProvider.getPlayingMusic();
            var receivedMusicInfo = {}
            if (music.state === 'PLAYING' || music.state === 'PAUSED') {
                this.state = music.state;
                receivedMusicInfo.state = music.state;
                receivedMusicInfo.volume = music.volume;
                receivedMusicInfo.songTitle = music.songTitle;
                receivedMusicInfo.artists = music.artists;
                receivedMusicInfo.album = music.album;
                receivedMusicInfo.playFrom = music.playFrom;
                receivedMusicInfo.imageUrl = music.imageUrl;
                receivedMusicInfo.songColor = music.songColor;
                this.music = receivedMusicInfo;
                this.callback(receivedMusicInfo);
                return true;
            } else {
                this.state = undefined;
                return false;
            }
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    // TODO: talk about the different cases. If db but no syb, should we still display playlists?
    // If syb but no db, should we add all the playlists? (that should be the hub's job)
    async getAvailablePlaylists() {
        try {
            const [dbResult, providerResult] = await Promise.allSettled([
                getPlaylists(this.customerId, this.musicPlayerId),
                this.playerProvider.getAvailablePlaylists()
            ]);

            const playlistsDb = dbResult.status === "fulfilled" ? dbResult.value : null;
            const playlistsProvider = providerResult.status === "fulfilled" ? providerResult.value : null;

            // If both exist, check if any playlists are added or deleted
            if (playlistsDb?.length && playlistsProvider?.length) {
                const dbRemoteIds = new Set(playlistsDb.map(p => p.remotePlaylistId));
                const providerIds = new Set(playlistsProvider.map(p => p.playlistId));

                const toAdd = playlistsProvider.filter(p => !dbRemoteIds.has(p.playlistId));
                const toDelete = playlistsDb.filter(p => !providerIds.has(p.remotePlaylistId));

                for (const playlist of toAdd) {
                    try {
                        const list = {
                            remotePlaylistId: playlist.playlistId,
                            customerId: this.customerId,
                            musicPlayerId: this.musicPlayerId,
                            name: playlist.name,
                            imageUrl: playlist.imageUrl,
                            moodScore: 0,
                            moodMatch: 0,
                            useInVirtualDj: false,
                        }
                        playlistsDb.push(list);
                        await addPlaylist(list);
                    } catch (err) {
                        console.error(`Failed to add playlist ${playlist.remotePlaylistId}:`, err);
                    }
                }

                for (const playlist of toDelete) {
                    try {
                        await deletePlaylist(playlist.playlistId);
                        const index = playlistsDb.findIndex(p => p.playlistId === playlist.playlistId);
                        if (index !== -1) {
                            playlistsDb.splice(index, 1);
                        }
                    } catch (err) {
                        console.error(`Failed to delete playlist ${playlist.playlistId}:`, err);
                    }
                }
            }

            // Prefer results from database since it has virtualDj information
            if (playlistsDb?.length) {
                this.hasStoredPlaylists = true;
                playlistsDb.forEach(list => { list.label = list.name });
                this.music.availablePlaylists = playlistsDb;
            } else if (playlistsProvider?.length) {
                playlistsProvider.forEach(list => {
                    list.label = list.name;
                    list.remotePlaylistId = list.playlistId;
                    list.playlistId = null;
                });
                this.music.availablePlaylists = playlistsProvider;
            } else return false;

            this.callback(this.music);
            return true;

        } catch (e) {
            console.error("Unexpected error in getAvailablePlaylists:", e);
            return false;
        }
    }

    updateAvailablePlaylists(updatedDbPlaylists) {
        this.music.availablePlaylists = updatedDbPlaylists;
        this.callback(this.music);
    }

    async getAvailableSchedules() {
        try {
            const schedules = await this.playerProvider.getAvailableSchedules();
            if (schedules) {
                schedules?.forEach(schedule => { schedule.label = schedule.name });
                this.music.availableSchedules = schedules;
                this.callback(this.music);
            }
        } catch (e) {
            return false;
        }
    }

    async playPlaylist(playlistID) {
        try {
            const result = this.playerProvider.playPlaylist(playlistID);
            if (result) return true;
            else return false;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async queuePlaylist(playlistID) {
        try {
            const result = this.playerProvider.queuePlaylist(playlistID);
            if (result) return true;
            else return false;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async checkAuthorization() {
        try {
            const result = await this.playerProvider.checkAuthorization();
            if (result) {
                this.isAuthorized = true;
                return true
            }
            else {
                this.isAuthorized = false;
                return false
            };
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async checkConnection() {
        try {
            const result = await this.playerProvider.checkConnection();
            if (result) {
                this.isConnected = true;
                return true
            }
            else {
                this.isConnected = false;
                return false
            };
        } catch (e) {
            console.log(e);
            return false;
        }

    }

    async wakeupPlayer() {
        try {
            const result = await this.playerProvider.wakeupPlayer();
            if (result) {
                return true;
            }
            else {
                return false;
            }
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async updateAuthorization() {
        try {
            const updatedTokens = await this.playerProvider.updateAuthorization();
            if (updatedTokens) {
                const newToken = updatedTokens.token;
                const newRefreshToken = updatedTokens.refreshToken;
                await updateMusicPlayer({
                    musicPlayerId: this.musicPlayerId,
                    token: newToken,
                    refreshToken: newRefreshToken
                });
                this.token = newToken;
                this.refreshToken = newRefreshToken;
                this.playerProvider = getPlayerProvider({ type: this.type, remoteId: this.remoteId, token: this.token, refreshToken: this.refreshToken });
            }
            return true;
        } catch (e) {
            console.log(e);
            this.isAuthorized = false;
        }
    }

    async subscribe() {
        try {
            const subscribing = await this.playerProvider.subscribe(this);
            if (subscribing) this.isSubscribing = true;
            return true;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async cleanup() {
        try {
            if (this.reinitiationInterval) clearInterval(this.reinitiationInterval);
            this.playerProvider.cleanup();
        } catch (e) {
            console.log(e);
        }

    }

    subscriptionCallback(data) {
        updatableProps.forEach(prop => {
            if (data?.[`${prop}`]) {
                this.music[`${prop}`] = data[`${prop}`];
            }
        })
        this.callback(this.music);
    }
}
export default MusicPlayer;